/**
 * ComparisonHeader
 *
 * Renders the two identity cards (A vs B), overall delta pill, Swap button,
 * DTW match quality badge, and course match warning banner.
 */

import GarminAttribution from '../../garmin/GarminAttribution';
import { Box, Typography, Button, Chip, Alert, useTheme, useMediaQuery, Tooltip, Checkbox, FormControlLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { tokens } from '../../../theme';

const COLOR_A = '#3b82f6';   // blue
const COLOR_B = '#f59e0b';   // amber

function formatDuration(seconds) {
  if (seconds == null) return '—';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}h ${m.toString().padStart(2, '0')}m`;
  return `${m}m ${s.toString().padStart(2, '0')}s`;
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
}

function formatDistance(meters) {
  if (meters == null) return '—';
  return `${(meters / 1000).toFixed(1)} km`;
}

function formatPace(minPerKm) {
  if (minPerKm == null) return '—';
  const m = Math.floor(minPerKm);
  const s = Math.round((minPerKm - m) * 60);
  return `${m}:${s.toString().padStart(2, '0')} /km`;
}

function WorkoutCard({ analysis, label, badge, color, filename, description, fileSource, deviceName, snapToGpx, onSnapToGpxChange, applyGpsCorrections, onApplyGpsCorrectionsChange, showControls }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();
  const raceName = analysis?.race?.name || analysis?.race?.event_name;

  // Determine if GPS corrections checkbox should be disabled
  const correctionsNotNeeded = analysis?.sanitization &&
    !analysis.sanitization.applied &&
    !analysis.sanitization.user_disabled;

  return (
    <Box
      flex={1}
      p={2}
      sx={{
        borderRadius: 2,
        border: `2px solid ${color}`,
        backgroundColor: colors.grey[800],
        minWidth: 0,
      }}
    >
      <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
        <Typography
          variant="caption"
          sx={{ color, fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1 }}
        >
          {label}
        </Typography>
        {badge && (
          <Chip
            label={badge}
            size="small"
            variant="outlined"
            sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, textTransform: 'uppercase',
                  letterSpacing: 0.5, color, borderColor: color, bgcolor: 'transparent' }}
          />
        )}
      </Box>
      {filename && (
        <Tooltip title={filename} placement="bottom-start">
          <Typography
            variant="caption"
            color={colors.grey[400]}
            display="block"
            noWrap
            sx={{ fontSize: '0.7rem', mt: 0.25, maxWidth: '100%', cursor: 'default' }}
          >
            {filename}
          </Typography>
        </Tooltip>
      )}
      <GarminAttribution source={fileSource} deviceName={deviceName} />
      {description && (
        <Typography
          variant="caption"
          color={colors.grey[500]}
          display="block"
          noWrap
          sx={{ fontSize: '0.7rem', maxWidth: '100%' }}
        >
          {description}
        </Typography>
      )}
      <Typography variant="body2" color={colors.grey[300]} mt={0.5}>
        {formatDate(analysis?.workout_start_time)}
      </Typography>
      {raceName && (
        <Typography variant="body2" color={colors.grey[300]} noWrap>
          {raceName}
        </Typography>
      )}
      <Box mt={1} display="flex" flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="caption" color={colors.grey[500]}>{t('distance', 'Distance')}</Typography>
          <Typography variant="body1" fontWeight="bold" color={colors.grey[100]}>
            {formatDistance(analysis?.total_distance)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color={colors.grey[500]}>{t('finishTimeShort', 'Finish time')}</Typography>
          <Typography variant="body1" fontWeight="bold" color={colors.grey[100]}>
            {formatDuration(analysis?.total_duration)}
          </Typography>
        </Box>
        <Box>
          <Typography variant="caption" color={colors.grey[500]}>{t('avgPace', 'Avg pace')}</Typography>
          <Typography variant="body1" fontWeight="bold" color={colors.grey[100]}>
            {formatPace(analysis?.avg_pace)}
          </Typography>
        </Box>
      </Box>

      {/* Parameter Controls */}
      {showControls && (
        <Box mt={2} pt={2} borderTop={`1px solid ${colors.grey[700]}`}>
          <Box display="flex" flexDirection="column" gap={0.5}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={snapToGpx}
                  onChange={(e) => onSnapToGpxChange(e.target.checked)}
                  size="small"
                  sx={{
                    color: colors.grey[500],
                    '&.Mui-checked': { color },
                    py: 0.5
                  }}
                />
              }
              label={<Typography variant="caption" color={colors.grey[400]}>{t('snapToGpx', 'Snap to GPX')}</Typography>}
              sx={{ m: 0 }}
            />
            <Tooltip
              title={correctionsNotNeeded ? t('gpsCorrectionsNotNeeded', 'GPS corrections not necessary for this workout') : ''}
              placement="right"
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={applyGpsCorrections}
                    onChange={(e) => onApplyGpsCorrectionsChange(e.target.checked)}
                    disabled={correctionsNotNeeded}
                    size="small"
                    sx={{
                      color: colors.grey[500],
                      '&.Mui-checked': { color },
                      '&.Mui-disabled': { color: colors.grey[700] },
                      py: 0.5
                    }}
                  />
                }
                label={<Typography variant="caption" color={colors.grey[400]}>{t('gpsCorrections', 'GPS Corrections')}</Typography>}
                sx={{ m: 0 }}
              />
            </Tooltip>
          </Box>
        </Box>
      )}
    </Box>
  );
}

function DeltaPill({ durationA, durationB, fromCheckpoints }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  if (durationA == null || durationB == null) return null;

  const deltaS = durationA - durationB;
  const abs = Math.abs(deltaS);
  const leader = deltaS > 0 ? 'B' : 'A';
  const leaderColor = leader === 'A' ? COLOR_A : COLOR_B;

  return (
    <Tooltip
      title={fromCheckpoints ? t('basedOnCheckpointTimes', 'Based on finish line checkpoint times') : ''}
      placement="bottom"
    >
      <Box textAlign="center" px={1} flexShrink={0}>
        <Typography variant="caption" color={colors.grey[500]}>{t('overall', 'Overall')}</Typography>
        <Typography variant="h5" fontWeight="bold" sx={{ color: leaderColor }}>
          {formatDuration(abs)}
        </Typography>
        <Typography variant="caption" color={colors.grey[400]}>
          {leader} {t('faster', 'faster')}
        </Typography>
      </Box>
    </Tooltip>
  );
}

function DtwBadge({ alignment }) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation();

  if (!alignment) return null;

  const { course_match: match, dtw_cost_m: cost } = alignment;
  const color =
    match === 'good' ? colors.greenAccent[500] :
    match === 'warn' ? '#f59e0b' :
    '#ef4444';

  const label =
    match === 'good' ? t('gpsMatchGood', 'GPS match: good') :
    match === 'warn' ? t('gpsMatchModerate', 'GPS match: moderate') :
    t('gpsMatchPoor', 'GPS match: poor');

  return (
    <Tooltip title={t('avgGpsDeviation', 'Avg GPS deviation: {{cost}} m', { cost: cost?.toFixed(0) })} placement="bottom">
      <Chip
        label={label}
        size="small"
        sx={{
          border: `1px solid ${color}`,
          color,
          backgroundColor: 'transparent',
          fontWeight: 'bold',
          fontSize: '0.7rem',
        }}
      />
    </Tooltip>
  );
}

export default function ComparisonHeader({
  analysisA,
  analysisB,
  alignment,
  checkpoints,
  labelA,
  labelB,
  descriptionA,
  descriptionB,
  // Parameter controls (optional - only shown when provided)
  snapToGpxA,
  onSnapToGpxAChange,
  applyGpsCorrectionsA,
  onApplyGpsCorrectionsAChange,
  snapToGpxB,
  onSnapToGpxBChange,
  applyGpsCorrectionsB,
  onApplyGpsCorrectionsBChange,
  showControls = false,
  // When B is the race's own prediction, label the cards explicitly (A = actual run, B = plan).
  bIsPrediction = false,
  sourceA = null,
  sourceB = null,
  deviceNameA = null,
  deviceNameB = null,
}) {
  const { t: _t } = useTranslation();
  const aBadge = bIsPrediction ? _t('actualRaceBadge', 'Actual race') : null;
  const bBadge = bIsPrediction ? _t('predictionBadge', 'Prediction') : null;
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const courseMatch = alignment?.course_match;

  // Use the last checkpoint's arrival times for the DeltaPill — these are the actual
  // race finish times from the checkpoint comparison, which correctly reflect when each
  // runner reached the finish line (not the raw FIT recording length).
  // Falls back to total_duration when no checkpoints are available.
  const lastCp = checkpoints?.length ? checkpoints[checkpoints.length - 1] : null;
  const finishTimeA = lastCp?.arrived_a_s ?? null;
  const finishTimeB = lastCp?.arrived_b_s ?? null;
  const deltaFromCheckpoints = finishTimeA != null && finishTimeB != null;

  const centerBlock = (
    <Box display="flex" flexDirection={isMobile ? 'row' : 'column'} alignItems="center" gap={isMobile ? 2 : 1} flexShrink={0} justifyContent={isMobile ? 'center' : undefined}>
      <DtwBadge alignment={alignment} />
      <DeltaPill
        durationA={deltaFromCheckpoints ? finishTimeA : analysisA?.total_duration}
        durationB={deltaFromCheckpoints ? finishTimeB : analysisB?.total_duration}
        fromCheckpoints={deltaFromCheckpoints}
      />
    </Box>
  );

  return (
    <Box>
      {/* Course match warning banner */}
      {courseMatch === 'warn' && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {t('gpsTracksDifferWarn', 'GPS tracks differ moderately (avg {{cost}} m). Comparison is approximate.', { cost: alignment.dtw_cost_m?.toFixed(0) })}
        </Alert>
      )}
      {courseMatch === 'different' && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {t('gpsTracksVeryDiff', 'These workouts may not follow the same course (avg {{cost}} m deviation). Alignment may be unreliable.', { cost: alignment.dtw_cost_m?.toFixed(0) })}
        </Alert>
      )}

      {/* On mobile: summary above, cards stacked below */}
      {isMobile ? (
        <Box display="flex" flexDirection="column" gap={1}>
          {centerBlock}
          <WorkoutCard
            analysis={analysisA}
            label="A (base)"
            badge={aBadge}
            color={COLOR_A}
            filename={labelA}
            description={descriptionA}
            fileSource={sourceA}
            deviceName={deviceNameA}
            snapToGpx={snapToGpxA}
            onSnapToGpxChange={onSnapToGpxAChange}
            applyGpsCorrections={applyGpsCorrectionsA}
            onApplyGpsCorrectionsChange={onApplyGpsCorrectionsAChange}
            showControls={showControls}
          />
          <WorkoutCard
            analysis={analysisB}
            label="B (compare)"
            badge={bBadge}
            color={COLOR_B}
            filename={labelB}
            description={descriptionB}
            fileSource={sourceB}
            deviceName={deviceNameB}
            snapToGpx={snapToGpxB}
            onSnapToGpxChange={onSnapToGpxBChange}
            applyGpsCorrections={applyGpsCorrectionsB}
            onApplyGpsCorrectionsChange={onApplyGpsCorrectionsBChange}
            showControls={showControls}
          />
        </Box>
      ) : (
        /* On desktop: A card | center | B card */
        <Box display="flex" alignItems="center" gap={1}>
          <WorkoutCard
            analysis={analysisA}
            label="A (base)"
            badge={aBadge}
            color={COLOR_A}
            filename={labelA}
            description={descriptionA}
            fileSource={sourceA}
            deviceName={deviceNameA}
            snapToGpx={snapToGpxA}
            onSnapToGpxChange={onSnapToGpxAChange}
            applyGpsCorrections={applyGpsCorrectionsA}
            onApplyGpsCorrectionsChange={onApplyGpsCorrectionsAChange}
            showControls={showControls}
          />
          {centerBlock}
          <WorkoutCard
            analysis={analysisB}
            label="B (compare)"
            badge={bBadge}
            color={COLOR_B}
            filename={labelB}
            description={descriptionB}
            fileSource={sourceB}
            deviceName={deviceNameB}
            snapToGpx={snapToGpxB}
            onSnapToGpxChange={onSnapToGpxBChange}
            applyGpsCorrections={applyGpsCorrectionsB}
            onApplyGpsCorrectionsChange={onApplyGpsCorrectionsBChange}
            showControls={showControls}
          />
        </Box>
      )}
    </Box>
  );
}
